/**
 * A live room's step 3: its page written from the host's notes. Five steps land
 * one by one (the server's own `readProgress`), and the page fills in beside them
 * as each lands: the title, the description, what the live will cover.
 */
import { LIVE_SCENE_ORDER, type SceneStep } from "../../../shared/board-copy";
import type { BoardState } from "../../../shared/types";
import { Icon } from "../../components/ui/Icon";
import { cx } from "../../components/ui/cx";
import { StepHead } from "./MakeFrame";
import type { StepAction } from "./MakeSteps";

export const LIVE_SCENE_NAMES: Record<string, string> = {
  read: "Reading your notes",
  title: "Picking the title",
  desc: "Writing the description",
  chapters: "Listing what you will cover",
  page: "Laying out the page",
};

/** Grey lines standing in for words not written yet. */
function sk(n: number, cls = "") {
  return Array.from({ length: n }, (_, i) => <i key={i} className={cx("sk", cls)} />);
}

export function StepWrite({
  readProgress,
  built,
  onNext,
  error,
  action,
}: {
  readProgress: SceneStep[] | null;
  built: BoardState | null;
  onNext: () => void;
  error?: string;
  action?: StepAction;
}) {
  const landed = new Set(readProgress ?? []);
  const done = built !== null;
  const running = LIVE_SCENE_ORDER.find((s) => !landed.has(s));
  const title = built?.page.find((c) => c.kind === "title");
  const desc = built?.page.find((c) => c.kind === "desc");
  const chapters = built?.page.find((c) => c.kind === "chapters");
  return (
    <div className="card nbw">
      <StepHead n={3} of={4} title={done ? "Your page is written" : "Writing the page"} sub={done ? undefined : "About a minute"} />
      <div className="nbwork">
        <div className="nbwrows">
          {LIVE_SCENE_ORDER.map((step) => {
            const st = done || landed.has(step) ? "done" : step === running ? "run" : "wait";
            return (
              <div className={cx("nbwrow", st)} key={step}>
                <span className="o">{st === "done" ? <Icon name="check" /> : st === "run" ? <span className="spin" /> : null}</span>
                <div>
                  <b>{LIVE_SCENE_NAMES[step]}</b>
                </div>
              </div>
            );
          })}
        </div>
        <div className="pv nbwmini">
          {title && title.kind === "title" ? <h3 className="ph1 in">{title.v}</h3> : <div className="skw">{sk(2, "h")}</div>}
          {desc && desc.kind === "desc" ? <p className="in">{desc.v}</p> : <div className="skw">{sk(3)}</div>}
          <div className="nbwmini-l">
            <b className="ph1">What we will cover</b>
            {chapters && chapters.kind === "chapters"
              ? chapters.rows.map((r) => (
                  <div className="in" key={`${r.t}-${r.title}`}>
                    <i>
                      <Icon name="check" />
                    </i>
                    {r.title}
                  </div>
                ))
              : sk(4)}
          </div>
        </div>
      </div>
      {error ? (
        <div className="note warn mt16" role="alert">
          <Icon name="info" />
          <div>
            <span>{error}</span>
            {action ? (
              <div className="row g8 mt8">
                <button type="button" className="btn s" onClick={action.onClick}>
                  {action.label}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      {done ? (
        <div className="nbw-f">
          <button type="button" className="btn p lg" onClick={onNext}>
            Design the page <Icon name="right" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
